import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Activity,
  Brain,
  Bell,
  Shield,
  ArrowRight,
  ArrowUpRight,
} from "lucide-react";
import { STATS_2025, VALUE_PROPS, SECTORS } from "../data/content";

const ICONS = { "01": Activity, "02": Brain, "03": Bell, "04": Shield };

function CountUp({ target, duration = 1400 }) {
  const [val, setVal] = useState("0");
  useEffect(() => {
    const start = performance.now();
    const isPercent = target.startsWith("%");
    const isMoney = target.startsWith("₺");
    // Strip currency/% prefix then treat dots as thousand separators (TR)
    const cleaned = target.replace(/[%₺M\s]/g, "");
    let numeric;
    if (isMoney) {
      // e.g. "513.4" => 513.4
      numeric = parseFloat(cleaned);
    } else if (cleaned.includes(".") && !cleaned.includes(",")) {
      // TR thousands "4.273" => 4273
      numeric = parseInt(cleaned.replace(/\./g, ""), 10);
    } else {
      numeric = parseFloat(cleaned.replace(",", "."));
    }
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const current = numeric * eased;
      let formatted;
      if (isPercent) formatted = `%${Math.round(current)}`;
      else if (isMoney) formatted = `₺${current.toFixed(1)}M`;
      else formatted = Math.round(current).toLocaleString("tr-TR");
      setVal(formatted);
      if (t < 1) requestAnimationFrame(tick);
      else setVal(target);
    };
    requestAnimationFrame(tick);
  }, [target, duration]);
  return <span>{val}</span>;
}

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative min-h-[88vh] overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />
        <div
          className="absolute inset-0 opacity-50 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at 80% 30%, rgba(255,90,0,0.25), transparent 55%), radial-gradient(ellipse at 10% 80%, rgba(230,25,25,0.15), transparent 60%)",
          }}
        />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-16 md:pt-28 pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 border border-thermal-orange/40 px-3 py-1.5 mb-8"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-thermal-orange pulse-marker" />
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-thermal-orange">
                  System Status — Active Scan
                </span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.05 }}
                className="text-[2.25rem] sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05]"
              >
                Görünenin <span className="thermal-text">ötesini</span> algılayan
                <br className="hidden sm:inline" />
                <span className="sm:hidden"> </span>
                yapay zeka çözümleri.
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.15 }}
                className="mt-5 sm:mt-7 text-slate-300 text-base sm:text-lg max-w-2xl leading-relaxed"
              >
                Gelişmiş termal analiz ve önleyici güvenlik çözümleri. Yangınları
                görünür hale gelmeden önce tespit eder, ekipman arızalarını
                oluşmadan önce bildirir.
              </motion.p>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.25 }}
                className="mt-8 sm:mt-10 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4"
              >
                <Link
                  to="/cozumler"
                  data-testid="hero-cta-solutions"
                  className="inline-flex items-center justify-center gap-2 bg-thermal-orange hover:bg-thermal-red text-white px-6 py-3.5 font-mono text-xs uppercase tracking-[0.2em] transition-colors min-h-[48px]"
                >
                  Çözümleri Keşfet <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/sektorler"
                  data-testid="hero-cta-sectors"
                  className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-thermal-orange hover:text-thermal-orange text-white px-6 py-3.5 font-mono text-xs uppercase tracking-[0.2em] transition-colors min-h-[48px]"
                >
                  Sektörler
                </Link>
                <a
                  href="/assets/zake-termal-brosur.pdf"
                  download
                  data-testid="hero-cta-brosur"
                  className="inline-flex items-center justify-center gap-2 border border-thermal-orange/50 hover:bg-thermal-orange/10 text-thermal-orange px-6 py-3.5 font-mono text-xs uppercase tracking-[0.2em] transition-colors min-h-[48px]"
                >
                  ↓ Broşür (PDF)
                </a>
              </motion.div>
            </div>

            {/* Thermal scope visual */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="lg:col-span-4 relative h-[280px] md:h-[340px] terminal-border scanline overflow-hidden"
            >
              <div className="absolute inset-0 thermal-gradient opacity-90" />
              <div className="absolute inset-0 bg-grid-sm opacity-30" />
              <div
                className="absolute left-0 right-0 h-px bg-thermal-orange/70 animate-scan"
                style={{ boxShadow: "0 0 16px 2px rgba(255,90,0,0.7)" }}
              />
              <div className="absolute inset-3 border border-thermal-orange/40 pointer-events-none" />
              <div className="absolute top-3 left-3 font-mono text-[10px] text-thermal-orange uppercase tracking-[0.2em]">
                THRM://scan-01
              </div>
              <div className="absolute bottom-3 left-3 right-3 flex justify-between font-mono text-[10px] text-thermal-yellow/80 uppercase">
                <span>T: -20°C / +150°C</span>
                <span className="text-thermal-red">●REC</span>
              </div>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/70">
                  Hot Spot Detected
                </div>
                <div className="font-mono text-3xl text-thermal-yellow mt-1 font-bold">
                  +87.4°C
                </div>
              </div>
            </motion.div>
          </div>

          {/* Value props */}
          <div className="mt-20 md:mt-28 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 border border-white/10">
            {VALUE_PROPS.map((v, i) => {
              const Icon = ICONS[v.code];
              return (
                <motion.div
                  key={v.code}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.07 }}
                  data-testid={`value-prop-${v.code}`}
                  className="group bg-[#06090F] p-6 md:p-8 hover:bg-[#0D131F] transition-colors relative"
                >
                  <div className="flex items-start justify-between mb-6">
                    <Icon className="w-6 h-6 text-thermal-orange" strokeWidth={1.5} />
                    <span className="font-mono text-[10px] text-slate-500 uppercase tracking-[0.2em]">
                      / {v.code}
                    </span>
                  </div>
                  <h3 className="text-lg md:text-xl font-semibold mb-2 group-hover:text-thermal-orange transition-colors">
                    {v.title}
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{v.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 2025 STATS */}
      <section className="relative py-20 md:py-28 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-thermal-orange mb-3">
                / 2025 Türkiye İstatistikleri
              </div>
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight">
                Her yangın önlenebilir
                <br />
                <span className="text-slate-400">— doğru teknoloji ile.</span>
              </h2>
            </div>
            <p className="text-slate-400 max-w-md text-sm">
              Geleneksel duman dedektörleri ve sprinkler sistemleri, yangın gelişim sürecinin
              tamamını kapsamaz. Müdahale, alev görünmeden başlamalıdır.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 border border-white/10">
            {STATS_2025.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                data-testid={`stat-${i}`}
                className="bg-[#06090F] p-8 md:p-10"
              >
                <div className="font-mono text-5xl md:text-6xl text-thermal-orange font-semibold tracking-tighter">
                  <CountUp target={s.value} />
                </div>
                <div className="mt-4 text-white font-medium">{s.label}</div>
                <div className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                  {s.sub}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Fire dev process bar */}
          <div className="mt-16">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-3">
              / Yangın Gelişim Süreci
            </div>
            <div className="grid grid-cols-5 gap-px bg-white/10 border border-white/10">
              {[
                { stage: "Isınma", color: "#10B981", label: "Termal Tespit" },
                { stage: "Sıcak Nokta", color: "#FACC15", label: "AI Algı" },
                { stage: "Duman", color: "#FF9500", label: "Geleneksel" },
                { stage: "Alev", color: "#FF5A00", label: "Geç müdahale" },
                { stage: "Büyük Zarar", color: "#E61919", label: "Kayıp" },
              ].map((step, i) => (
                <div key={i} className="bg-[#06090F] p-5">
                  <div
                    className="font-mono text-[10px] uppercase tracking-[0.2em]"
                    style={{ color: step.color }}
                  >
                    Faz 0{i + 1}
                  </div>
                  <div className="mt-2 font-semibold text-white">{step.stage}</div>
                  <div className="mt-1 text-xs text-slate-500">{step.label}</div>
                  <div
                    className="mt-4 h-1 w-full"
                    style={{ backgroundColor: step.color, opacity: 0.7 }}
                  />
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm text-slate-400 max-w-3xl">
              <span className="text-thermal-orange font-mono text-xs">→</span> Termal Algılama
              Sistemi ilk aşamada (Isınma) devreye girer. Geleneksel sistemler Duman aşamasını
              bekler — bu fark, can ve mal güvenliği demektir.
            </p>
          </div>
        </div>
      </section>

      {/* SECTOR PREVIEW */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-thermal-orange mb-3">
                / Uygulama Alanları
              </div>
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight">
                8 sektörde önleyici güvenlik
              </h2>
            </div>
            <Link
              to="/sektorler"
              data-testid="home-sectors-link"
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-thermal-orange hover:text-thermal-yellow transition-colors"
            >
              Tüm sektörleri gör <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {SECTORS.slice(0, 8).map((s) => (
              <Link
                key={s.id}
                to="/sektorler"
                data-testid={`home-sector-${s.id}`}
                className="bg-[#06090F] p-5 md:p-6 hover:bg-[#0D131F] group transition-colors"
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">
                  / {s.category}
                </div>
                <div className="text-white font-semibold leading-tight group-hover:text-thermal-orange transition-colors">
                  {s.title}
                </div>
                <div className="mt-4 font-mono text-thermal-orange text-lg">
                  {s.metric.value}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                  {s.metric.label}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-y border-white/10 py-14 sm:py-16 md:py-20 bg-[#0D131F]/40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-thermal-orange mb-3">
              / Hızlı Başlangıç
            </div>
            <h3 className="text-2xl md:text-4xl font-bold tracking-tight max-w-xl">
              Tesisinizin termal risk skorunu 30 saniyede öğrenin.
            </h3>
          </div>
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 w-full md:w-auto">
            <Link
              to="/risk-hesaplayici"
              data-testid="home-cta-calculator"
              className="inline-flex items-center justify-center gap-2 bg-thermal-orange hover:bg-thermal-red text-white px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] transition-colors min-h-[48px]"
            >
              Risk Skoru Hesapla <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/iletisim"
              data-testid="bottom-cta-contact"
              className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-thermal-orange hover:text-thermal-orange text-white px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] transition-colors min-h-[48px]"
            >
              İletişime Geç
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section — visible answers help SEO + LLMs */}
      <section className="py-16 md:py-24" id="faq" data-testid="home-faq">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-thermal-orange mb-3">
            / Sıkça Sorulan Sorular
          </div>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-10">
            Termal güvenlik hakkında her şey
          </h2>
          <div className="space-y-px bg-white/5 border border-white/10">
            {[
              {
                q: "Termal kamera ile yangın nasıl önlenir?",
                a: "Termal kameralar, yangın gelişiminin ilk aşaması olan 'ısınma' fazında ekipman ve yüzeylerdeki anormal sıcaklık artışlarını tespit eder. Duman veya alev oluşmadan önce, yapay zeka destekli analiz ile saniyeler içinde uyarı verir; sprinkler, havalandırma ve SCADA sistemlerini otomatik tetikler. Bu sayede %78'e varan oranda önlenebilir yangın engellenir.",
              },
              {
                q: "GES tesislerinde termal denetim zorunlu mu?",
                a: "Evet. 50 kWp ve üzeri tüm güneş enerji santrallerinde TSE standartlarına göre yılda en az 2 kez termal denetim zorunludur. Sürekli termal izleme; hot-spot, hücre arızası ve bypass diyot hatalarını anlık tespit ederek yasal uyumluluğu otomatik sağlar.",
              },
              {
                q: "Mevcut SCADA / PLC sistemimle entegre olur mu?",
                a: "Evet. ZAKE termal sistemi endüstri standardı Modbus TCP/IP, OPC UA, MQTT ve REST API protokollerini destekler. PLC, SCADA, BMS ve ERP sistemleriyle sorunsuz haberleşir; mevcut altyapınıza ek donanım gerektirmeden katmanlanır.",
              },
              {
                q: "Kestirimci bakımda hangi kazanımları sağlıyor?",
                a: "Elektrik motorlarında aşırı ısınma, rulman ve mil yataklarında sürtünme kaynaklı sıcaklık artışları saniyeler içinde algılanır. Plansız duruşlarda %70-85 azalma, bakım maliyetlerinde %40'a varan tasarruf ve ekipman ömründe %30-50 uzama görülmektedir.",
              },
              {
                q: "Sera ve tarımda termal görüntüleme ne işe yarar?",
                a: "Sera tarımında termal kameralar bitki yaprak sıcaklığından su stresi ve hastalık erken belirtilerini semptom öncesinde tespit eder. Isıtma sistemi (boyler, sıcak su boruları) verimini izler, don önleme için kritik eşikte perde/fan/ısıtıcıyı otomatik tetikler ve sulama optimizasyonu sağlar — verim artışı %25'e ulaşabilir.",
              },
              {
                q: "Hangi sektörlerde uygulanabilir?",
                a: "Endüstriyel üretim tesisleri, GES (güneş paneli), depolama ve lojistik, atık yönetimi ve geri dönüşüm, otoyol ve tüneller, orman yangın izleme, otel ve konaklama tesisleri ile sera ve bitkisel tarım dahil 8 ana sektörde uygulanmaktadır.",
              },
            ].map((item, i) => (
              <details
                key={i}
                data-testid={`faq-item-${i}`}
                className="bg-[#06090F] group p-5 md:p-6 [&[open]]:bg-[#0D131F] transition-colors"
              >
                <summary className="cursor-pointer flex items-start justify-between gap-4 list-none">
                  <div className="flex items-start gap-4 flex-1 min-w-0">
                    <span className="font-mono text-thermal-orange text-xs mt-1 shrink-0">
                      Q{String(i + 1).padStart(2, "0")}
                    </span>
                    <h3 className="text-base md:text-lg font-semibold text-white leading-snug">
                      {item.q}
                    </h3>
                  </div>
                  <span className="font-mono text-thermal-orange text-xl group-open:rotate-45 transition-transform shrink-0 leading-none">
                    +
                  </span>
                </summary>
                <p className="mt-4 ml-0 md:ml-12 text-slate-300 text-sm md:text-base leading-relaxed">
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
